/* HouseMouse web app. Plain CSS with variables; dark mode follows the
   system setting unless this device chose (설정 → 화면, lib/theme.ts sets
   data-theme on <html>). Korean text breaks between words (design 7.3). */
:root {
  --bg: #f3f5f8;
  --panel: #ffffff;
  --text: #1d2733;
  --muted: #6b7785;
  --line: #e2e7ee;
  --primary: #3c5a78;
  --primary-text: #ffffff;
  --danger: #c62828;
  --ok: #2e7d32;
  --chat-bg: #c7d5e2;
  --mine: #fde68a;
  --mine-text: #1d2733;
  --theirs: #ffffff;
  --theirs-text: #1d2733;
  --badge: #e5484d;
  --unread-count: #c58b00;
  --shadow: 0 1px 2px rgb(0 0 0 / 8%), 0 4px 16px rgb(0 0 0 / 8%);
  --radius: 14px;
  --sep-bg: rgb(0 0 0 / 14%);
  --sep-text: #fff;
  --unfinished-bg: #fff4d6;
  --unfinished-text: #4a3b00;
  --upload-bg: #dbe8f5;
  --upload-text: #1d3550;
  --stamp-bg: rgb(255 255 255 / 70%);
  --soon-bg: #fde68a;
  --soon-text: #8a5a00;
  /* a room's background (ui/background.tsx): how much of its color */
  --tint-share: 55%;
  --photo-veil: rgb(255 255 255 / 12%);
  color-scheme: light dark;
  font-family: -apple-system, 'Segoe UI', 'Malgun Gothic', 'Apple SD Gothic Neo', 'Noto Sans KR', Roboto, sans-serif;
  font-size: 16px;
  line-height: 1.45;
  -webkit-text-size-adjust: 100%;
}

/* Dark: the system's, unless this device chose light. The same values
   again below for a device that chose dark: keep the two the same. */
@media (prefers-color-scheme: dark) {
  :root:not([data-theme='light']) {
    --bg: #12171d;
    --panel: #1b222b;
    --text: #e6ebf1;
    --muted: #93a0ae;
    --line: #2b3440;
    --primary: #7aa3cc;
    --primary-text: #0f1720;
    --danger: #ff6b6b;
    --ok: #7bd389;
    --chat-bg: #151c24;
    --mine: #c7a529;
    --mine-text: #111;
    --theirs: #27313d;
    --theirs-text: #e6ebf1;
    --unread-count: #e0b84a;
    --sep-bg: rgb(255 255 255 / 10%);
    --sep-text: var(--muted);
    --unfinished-bg: #3a3220;
    --unfinished-text: #f3e3b5;
    --upload-bg: #1f3347;
    --upload-text: #dbe8f5;
    --stamp-bg: rgb(0 0 0 / 55%);
    --soon-bg: #e0b84a;
    --soon-text: #1d2733;
    --tint-share: 20%;
    --photo-veil: rgb(0 0 0 / 45%);
  }
}

:root[data-theme='dark'] {
  --bg: #12171d;
  --panel: #1b222b;
  --text: #e6ebf1;
  --muted: #93a0ae;
  --line: #2b3440;
  --primary: #7aa3cc;
  --primary-text: #0f1720;
  --danger: #ff6b6b;
  --ok: #7bd389;
  --chat-bg: #151c24;
  --mine: #c7a529;
  --mine-text: #111;
  --theirs: #27313d;
  --theirs-text: #e6ebf1;
  --unread-count: #e0b84a;
  --sep-bg: rgb(255 255 255 / 10%);
  --sep-text: var(--muted);
  --unfinished-bg: #3a3220;
  --unfinished-text: #f3e3b5;
  --upload-bg: #1f3347;
  --upload-text: #dbe8f5;
  --stamp-bg: rgb(0 0 0 / 55%);
  --soon-bg: #e0b84a;
  --soon-text: #1d2733;
  --tint-share: 20%;
  --photo-veil: rgb(0 0 0 / 45%);
  color-scheme: dark;
}

:root[data-theme='light'] {
  color-scheme: light;
}

* {
  box-sizing: border-box;
}

html,
body,
#root {
  margin: 0;
  height: 100%;
  background: var(--bg);
  color: var(--text);
}

body {
  overflow: hidden;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

button,
input,
select,
textarea {
  font: inherit;
  color: inherit;
}

a {
  color: var(--primary);
}

.app {
  height: 100dvh;
  display: flex;
  flex-direction: column;
}

.app.center {
  align-items: center;
  justify-content: center;
}

.split {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(300px, 360px) 1fr;
}

.split > aside {
  border-right: 1px solid var(--line);
  min-height: 0;
  display: flex;
}

.split > main,
main.single {
  min-height: 0;
  min-width: 0;
  flex: 1;
  display: flex;
}

.panel {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--panel);
}

.bar {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 56px;
  padding: 0 8px 0 12px;
  padding-top: env(safe-area-inset-top);
  border-bottom: 1px solid var(--line);
  background: var(--panel);
}

.bar h2 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  min-width: 0;
}

.spacer {
  flex: 1;
}

.icon-btn {
  border: 0;
  background: transparent;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text);
}

.icon-btn:hover {
  background: var(--line);
}

.icon {
  display: block;
}

.page {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
}

.card {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.card h3 {
  margin: 0;
  font-size: 1.05rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 0.9rem;
  font-weight: 600;
}

.field-hint,
.muted {
  color: var(--muted);
}

.small,
.field-hint {
  font-size: 0.85rem;
}

input[type='text'],
input[type='password'],
input[type='date'],
input[type='time'],
input:not([type]),
select,
textarea {
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--bg);
  min-height: 44px;
  min-width: 0;
}

input:focus-visible,
select:focus-visible,
textarea:focus-visible,
button:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}

button.primary,
button.secondary,
button.danger-btn,
a.secondary {
  min-height: 46px;
  border-radius: 12px;
  border: 0;
  padding: 0 18px;
  font-weight: 700;
  cursor: pointer;
}

button.primary,
a.primary {
  background: var(--primary);
  color: var(--primary-text);
}

/* a link that looks like the main button (the location helper's intent link) */
a.primary {
  display: inline-flex;
  align-items: center;
  min-height: 46px;
  border-radius: 12px;
  padding: 0 18px;
  font-weight: 700;
  text-decoration: none;
}

button.secondary,
a.secondary {
  background: var(--line);
}

/* a link that looks like a button (an sms: link) */
a.secondary {
  display: inline-flex;
  align-items: center;
  color: var(--text);
  text-decoration: none;
}

button.danger-btn {
  background: transparent;
  border: 1px solid var(--danger);
  color: var(--danger);
}

button:disabled {
  opacity: 0.5;
  cursor: default;
}

button.link {
  border: 0;
  background: none;
  color: var(--primary);
  padding: 4px 6px;
  cursor: pointer;
  font-weight: 600;
}

.danger,
button.link.danger {
  color: var(--danger);
}

.error {
  color: var(--danger);
  margin: 0;
}

.ok {
  color: var(--ok);
  margin: 0;
}

.notice {
  background: var(--bg);
  border-radius: 10px;
  padding: 10px 12px;
  margin: 0;
}

.check {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* a row of radio choices (settings: the theme, the text size) */
.radio-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin: 0;
  padding: 0;
  border: 0;
}

.radio-row legend {
  padding: 0;
  margin-bottom: 8px;
  font-weight: 600;
}

.radio-choice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  cursor: pointer;
}

/* quiet hours of this device (design 01a 4), under their checkbox */
.quiet-hours {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 26px;
}

.quiet-hours p {
  margin: 0;
}

.quiet-times {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.tag {
  font-size: 0.75rem;
  border-radius: 6px;
  background: var(--line);
  padding: 1px 6px;
  margin-left: 6px;
  vertical-align: middle;
}

.tag.danger {
  color: var(--danger);
}

.row {
  display: flex;
  gap: 8px;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.list-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  border-top: 1px solid var(--line);
}

.list-row:first-child {
  border-top: 0;
}

.grow {
  flex: 1;
  min-width: 0;
}

.actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--muted);
  padding: 24px;
  text-align: center;
}

.welcome-pane img {
  opacity: 0.85;
}

/* ---- banners ---- */

.banner {
  padding: 8px 12px;
  text-align: center;
  font-size: 0.9rem;
}

.banner.offline {
  background: #ffe9a8;
  color: #4a3b00;
}

.banner.update {
  background: var(--primary);
  color: var(--primary-text);
}

.banner.update .link {
  color: inherit;
  text-decoration: underline;
}

/* ---- auth ---- */

.auth {
  height: 100dvh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  gap: 20px;
}

.auth .card {
  width: 100%;
  max-width: 400px;
}

.brand {
  text-align: center;
}

.brand h1 {
  margin: 8px 0 0;
  font-size: 1.6rem;
}

.brand p {
  margin: 4px 0 0;
  color: var(--muted);
}

.welcome {
  margin: 0;
  font-weight: 600;
}

/* ---- avatars ---- */

.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 38%;
  color: #fff;
  font-weight: 700;
  flex: none;
  user-select: none;
}

/* a profile or room photo (design 01a 2, 9): the thumbnail cut to the same shape */
.avatar.photo {
  overflow: hidden;
  background: var(--line);
}

.avatar.photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bar > .avatar {
  margin-right: 6px;
}

.profile-photo {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin: 4px 0 10px;
}

.profile-photo-actions,
.busy-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* ---- room list ---- */

.rooms {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
}

.room-row {
  width: 100%;
  border: 0;
  background: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  text-align: left;
  cursor: pointer;
}

.room-row:hover {
  background: var(--bg);
}

.room-row.active {
  background: var(--line);
}

.room-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.room-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  min-width: 0;
}

.room-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.room-count {
  color: var(--muted);
  font-weight: 400;
  font-size: 0.9rem;
}

.room-muted {
  color: var(--muted);
  display: inline-flex;
}

.room-muted .icon {
  width: 14px;
  height: 14px;
}

.room-pinned {
  color: var(--primary);
}

/* "나와의 채팅" on the new chat screen (design 01a 2) */
.self-chat {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.room-preview {
  color: var(--muted);
  font-size: 0.92rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.room-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex: none;
}

.room-time {
  color: var(--muted);
  font-size: 0.78rem;
}

.badge {
  background: var(--badge);
  color: #fff;
  border-radius: 999px;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  font-size: 0.78rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.badge.muted {
  background: var(--muted);
}

/* ---- room view ---- */

.room-header-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.user-status {
  margin: 0;
  color: var(--muted);
  font-size: 0.8rem;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.person-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.room-header-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.messages-wrap {
  flex: 1;
  min-height: 0;
  position: relative;
  background: var(--chat-bg);
}

.messages {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  overscroll-behavior: contain;
}

.messages-spacer {
  flex-grow: 1;
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.room-start {
  color: var(--muted);
  font-size: 0.85rem;
  margin: 16px 0 8px;
}

.day-sep,
.unread-sep,
.system-msg {
  display: flex;
  justify-content: center;
  padding: 10px 12px 6px;
}

.day-sep span,
.system-msg span {
  background: var(--sep-bg);
  color: var(--sep-text);
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 0.8rem;
  text-align: center;
}

.unread-sep span {
  font-size: 0.8rem;
  color: var(--primary);
  background: var(--panel);
  border-radius: 999px;
  padding: 3px 12px;
  font-weight: 700;
}

.msg {
  display: flex;
  gap: 8px;
  padding: 2px 10px;
}

.msg.first {
  padding-top: 8px;
}

.msg.mine {
  justify-content: flex-end;
}

.msg-avatar {
  width: 38px;
  flex: none;
}

.msg-col {
  display: flex;
  flex-direction: column;
  max-width: min(78%, 560px);
  min-width: 0;
}

.msg.mine .msg-col {
  align-items: flex-end;
}

.msg-sender {
  font-size: 0.85rem;
  margin: 0 0 3px 2px;
}

.msg-line {
  display: flex;
  align-items: flex-end;
  gap: 5px;
  min-width: 0;
}

.msg.mine .msg-line {
  flex-direction: row-reverse;
}

.bubble {
  padding: 8px 12px;
  border-radius: 16px;
  background: var(--theirs);
  color: var(--theirs-text);
  min-width: 0;
  white-space: pre-wrap;
  word-break: keep-all;
  overflow-wrap: anywhere;
  -webkit-touch-callout: none;
  user-select: text;
}

.msg.theirs.first .bubble {
  border-top-left-radius: 4px;
}

.msg.mine .bubble {
  background: var(--mine);
  color: var(--mine-text);
}

.msg.mine.first .bubble {
  border-top-right-radius: 4px;
}

.bubble.pending {
  opacity: 0.7;
}

.bubble.failed {
  outline: 2px solid var(--danger);
}

.text.deleted,
.text.muted {
  color: var(--muted);
  font-style: italic;
}

.quote {
  display: flex;
  flex-direction: column;
  border-left: 3px solid rgb(0 0 0 / 25%);
  padding: 2px 0 4px 8px;
  margin-bottom: 6px;
  font-size: 0.85rem;
  opacity: 0.85;
  white-space: normal;
}

/* a reply's quote opens the message it quotes (design 01a 2) */
button.quote {
  width: 100%;
  border: 0;
  border-left: 3px solid rgb(0 0 0 / 25%);
  background: none;
  color: inherit;
  font: inherit;
  font-size: 0.85rem;
  text-align: left;
  cursor: pointer;
}

button.quote:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.msg.flash .bubble {
  animation: msg-flash 1.6s ease-out;
}

@keyframes msg-flash {
  0%,
  40% {
    box-shadow: 0 0 0 3px var(--primary);
  }
  100% {
    box-shadow: 0 0 0 3px transparent;
  }
}

.quote-name {
  font-weight: 700;
}

.quote-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.msg-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 0.72rem;
  color: var(--muted);
  flex: none;
  line-height: 1.2;
}

.msg.theirs .msg-meta {
  align-items: flex-start;
}

.unread-count {
  color: var(--unread-count);
  font-weight: 700;
}

.edited {
  white-space: nowrap;
}

/* the room's notice above the chat (design 01a 6) */
.notice-bar {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--line);
  background: var(--panel);
}

.notice-open {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 8px 4px 8px 14px;
  border: 0;
  background: none;
  color: var(--text);
  font: inherit;
  font-size: 0.92rem;
  text-align: left;
  cursor: pointer;
}

.notice-open .icon,
.notice-unfold .icon {
  flex: none;
  color: var(--primary);
}

.notice-text {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* folded: a small button over the top right of the messages */
.notice-unfold {
  position: absolute;
  top: 8px;
  right: 12px;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 50%;
  background: var(--panel);
  box-shadow: 0 1px 4px rgb(0 0 0 / 0.12);
  cursor: pointer;
}

.notice-full {
  margin: 0 0 4px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* "@이름" in a message, and the names offered while typing (design 01a 2) */
.mention {
  color: var(--primary);
  font-weight: 700;
}

.mention.me {
  padding: 0 2px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--primary) 18%, transparent);
}

.mention-choices {
  list-style: none;
  margin: 0 0 6px;
  padding: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
}

.mention-choices button {
  border: 0;
  border-radius: 999px;
  padding: 6px 12px;
  background: var(--bg);
  color: var(--text);
  font: inherit;
  cursor: pointer;
}

.mention-choices li[aria-selected='true'] button {
  background: var(--primary);
  color: var(--primary-text);
}

/* reactions under a bubble (design 01a 5) */
.reactions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;
  padding: 2px 6px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--panel);
  color: var(--text);
  font: inherit;
  font-size: 0.8rem;
  line-height: 1.4;
  cursor: pointer;
}

.reaction {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

/* mine */
.reaction.mine {
  margin: 0 -2px;
  padding: 0 2px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--primary) 18%, transparent);
}

.reaction-count {
  font-variant-numeric: tabular-nums;
}

/* the six at the top of the message menu */
.reaction-bar {
  display: flex;
  justify-content: space-between;
  gap: 4px;
  padding: 0 0 8px;
}

.reaction-bar button {
  flex: 1;
  min-width: 0;
  padding: 8px 0;
  border: 1px solid transparent;
  border-radius: 12px;
  background: var(--bg);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
}

.reaction-bar button[aria-pressed='true'] {
  border-color: var(--primary);
  background: color-mix(in srgb, var(--primary) 14%, transparent);
}

.reaction-people {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
}

.reaction-people li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px;
  border-bottom: 1px solid var(--line);
}

.reaction-emoji {
  font-size: 1.2rem;
}

.sending {
  color: var(--muted);
}

.failed-actions {
  display: flex;
  flex-direction: column;
}

.pending-error {
  color: var(--danger);
  font-size: 0.8rem;
  margin-top: 2px;
}

.to-bottom {
  position: absolute;
  right: 14px;
  bottom: 14px;
  border: 0;
  border-radius: 999px;
  background: var(--panel);
  box-shadow: var(--shadow);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  cursor: pointer;
}

.to-bottom-count {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--primary);
}

/* ---- composer ---- */

.composer {
  border-top: 1px solid var(--line);
  background: var(--panel);
  padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
}

.composer-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.composer textarea {
  flex: 1;
  resize: none;
  min-height: 44px;
  max-height: 40vh;
  line-height: 1.4;
  border-radius: 20px;
  padding: 10px 14px;
}

.composer .send {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 0;
  background: var(--primary);
  color: var(--primary-text);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: none;
}

.reply-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 8px;
}

.reply-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-left: 3px solid var(--primary);
  padding-left: 8px;
}

.reply-to {
  font-size: 0.85rem;
  font-weight: 700;
}

.reply-text {
  font-size: 0.85rem;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---- sheets ---- */

.sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgb(0 0 0 / 40%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  /* above the photo viewer (20): "드라이브에 저장" opens over it */
  z-index: 30;
}

.sheet {
  background: var(--panel);
  width: 100%;
  max-width: 480px;
  max-height: 85dvh;
  overflow-y: auto;
  border-radius: 18px 18px 0 0;
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
  box-shadow: var(--shadow);
}

/* The sheet takes the focus when it opens (to start the keyboard there);
   it is not something to press, so no ring around all of it. */
.sheet:focus {
  outline: none;
}

@media (min-width: 900px) {
  .sheet-backdrop {
    align-items: center;
  }

  .sheet {
    border-radius: 18px;
  }
}

.sheet-title {
  font-weight: 700;
  margin-bottom: 12px;
}

.share-out {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 8px 0 4px;
}

.share-out p {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.menu > button:not(.primary, .secondary) {
  border: 0;
  background: none;
  text-align: left;
  padding: 14px 8px;
  border-radius: 10px;
  cursor: pointer;
}

.menu > button:not(.primary, .secondary):hover {
  background: var(--bg);
}

.member-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.member-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--bg);
  border-radius: 999px;
  padding: 3px 10px 3px 3px;
  font-size: 0.9rem;
}

/* the room menu's members, with "내보내기" for the room's maker and admins */
.member-rows {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  max-height: 40vh;
  overflow-y: auto;
}

.member-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 2px;
}

.member-name {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.user-devices .list {
  margin-top: 4px;
}

/* ---- people picker ---- */

.picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.people {
  list-style: none;
  margin: 0;
  padding: 0;
}

.person {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px;
  cursor: pointer;
}

.person input {
  width: 20px;
  height: 20px;
}

.linkbox {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.linkbox p {
  margin: 0;
}

/* ---- spinner ---- */

.spinner {
  display: flex;
  justify-content: center;
  padding: 24px;
}

.spinner span {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid var(--line);
  border-top-color: var(--primary);
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner span {
    animation-duration: 2.4s;
  }
}

.push-prompt {
  margin: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.push-prompt p {
  margin: 0;
}

/* ---- files in chat (design 7.3) ---- */

.room-view {
  position: relative;
}

.composer .attach {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 0;
  background: var(--bg);
  color: var(--text);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: none;
}

.menu > button:not(.primary, .secondary) {
  display: flex;
  align-items: center;
  gap: 10px;
}

.menu-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 8px;
  border-radius: 10px;
  color: inherit;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menu-link:hover {
  background: var(--bg);
}

/* photos and videos only: no bubble (beats .msg.mine .bubble) */
.msg .bubble.media {
  padding: 0;
  background: transparent;
  border-radius: 14px;
  overflow: hidden;
  white-space: normal;
}

.bubble .media-layout + .file-card,
.bubble .quote + .media-layout {
  margin-top: 6px;
}

.media-layout {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-radius: 12px;
  overflow: hidden;
  max-width: 100%;
}

.media-row {
  display: flex;
  gap: 2px;
}

.media-slot {
  flex: none;
}

.media-cell {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  background: var(--line);
  overflow: hidden;
  cursor: pointer;
  color: inherit;
}

.media-cell img,
.media-cell video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-none {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--muted);
}

.media-badge {
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgb(0 0 0 / 55%);
  color: #fff;
  font-size: 0.72rem;
  line-height: 1.4;
}

.media-progress {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(0 0 0 / 35%);
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
}

.file-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: min(220px, 60vw);
  max-width: 280px;
  white-space: normal;
}

.bubble .file-card + .file-card {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgb(0 0 0 / 10%);
}

.file-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.file-icon {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--primary);
  color: var(--primary-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.file-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.file-name {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
}

.file-size {
  font-size: 0.8rem;
  opacity: 0.7;
}

.file-card audio {
  width: 100%;
  height: 36px;
}

.file-progress {
  width: 100%;
  height: 6px;
}

.sending-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.unfinished,
.composer-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 0.85rem;
  border-top: 1px solid var(--line);
}

.unfinished {
  background: var(--unfinished-bg);
  color: var(--unfinished-text);
}

.composer-error {
  background: var(--panel);
  color: var(--danger);
  justify-content: space-between;
}

.unfinished p {
  margin: 0;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.unfinished-names {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unfinished .hint {
  opacity: 0.8;
}

.unfinished-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.unfinished .link {
  color: inherit;
  text-decoration: underline;
}

.drop-zone {
  position: absolute;
  inset: 8px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 3px dashed rgb(255 255 255 / 75%);
  border-radius: 16px;
  background: rgb(60 90 120 / 85%);
  color: #fff;
  font-weight: 700;
  pointer-events: none;
}

.send-list {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  max-height: 50dvh;
  overflow-y: auto;
}

.send-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

.send-list img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
  flex: none;
}

.send-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.send-size {
  color: var(--muted);
  font-size: 0.85rem;
}

.banner.upload {
  background: var(--upload-bg);
  color: var(--upload-text);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.banner-hint {
  font-size: 0.8rem;
  opacity: 0.85;
}

/* ---- link previews (design 01a 6) ---- */

.link-card {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  width: 280px;
  max-width: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: rgb(0 0 0 / 5%);
  color: inherit;
  text-decoration: none;
  white-space: normal;
}

.link-card img {
  display: block;
  width: 100%;
  max-height: 180px;
  object-fit: cover;
  background: rgb(0 0 0 / 8%);
}

.link-card-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  min-width: 0;
}

.link-site {
  font-size: 0.75rem;
  opacity: 0.7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-title,
.link-desc {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow-wrap: anywhere;
}

.link-title {
  font-weight: 700;
}

.link-desc {
  font-size: 0.85rem;
  opacity: 0.8;
}

/* ---- voice messages (design 01a 7) ---- */

.voice {
  display: flex;
  align-items: center;
  gap: 10px;
  width: min(250px, 60vw);
  white-space: normal;
}

.voice-play {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 0;
  background: var(--primary);
  color: var(--primary-text);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.voice.pending .voice-play {
  opacity: 0.6;
  cursor: default;
}

.voice-wave {
  flex: 1;
  min-width: 0;
  height: 30px;
  display: flex;
  align-items: center;
  gap: 1px;
  cursor: pointer;
  border-radius: 4px;
}

.voice-wave:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.voice-wave span {
  flex: 1;
  min-width: 1px;
  border-radius: 1px;
  background: currentColor;
  opacity: 0.3;
}

.voice-wave span.on {
  opacity: 0.9;
}

.voice-time {
  flex: none;
  min-width: 2.6em;
  text-align: right;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
}

/* in place of the input while recording */
.recording {
  flex: 1;
  min-width: 0;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.rec-dot {
  flex: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--danger);
  animation: rec-blink 1.2s steps(2, jump-none) infinite;
}

@keyframes rec-blink {
  to {
    opacity: 0.2;
  }
}

.rec-time {
  flex: none;
  font-variant-numeric: tabular-nums;
}

.rec-level {
  flex: 1;
  min-width: 0;
  height: 6px;
  border-radius: 3px;
  background: var(--line);
  overflow: hidden;
}

.rec-level span {
  display: block;
  height: 100%;
  background: var(--primary);
  transform-origin: left;
  transition: transform 0.1s linear;
}

/* stopped by itself (in the background, or at the time limit) */
.recording.stopped .rec-dot {
  background: var(--muted);
  animation: none;
}

.rec-note {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
  color: var(--muted);
}

@media (prefers-reduced-motion: reduce) {
  .rec-dot {
    animation: none;
  }

  .rec-level span {
    transition: none;
  }
}

.composer .send.mic {
  background: var(--bg);
  color: var(--text);
}

/* ---- photo viewer ---- */

.viewer {
  position: fixed;
  inset: 0;
  z-index: 20;
  background: #000;
  color: #fff;
}

.viewer-stage {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.viewer-stage.zoomable {
  touch-action: none;
}

.viewer-stage img,
.viewer-stage video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}

.viewer-bar {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  transition: opacity 0.2s;
}

.viewer-bar.top {
  top: 0;
  padding-top: max(8px, env(safe-area-inset-top));
  background: linear-gradient(rgb(0 0 0 / 65%), transparent);
}

.viewer-bar.bottom {
  bottom: 0;
  padding-bottom: max(10px, env(safe-area-inset-bottom));
  background: linear-gradient(transparent, rgb(0 0 0 / 65%));
}

.viewer .icon-btn {
  color: #fff;
}

.viewer .icon-btn:hover {
  background: rgb(255 255 255 / 15%);
}

.viewer.bare .viewer-bar,
.viewer.bare .viewer-nav {
  opacity: 0;
  pointer-events: none;
}

.viewer-title {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.viewer-sender {
  font-weight: 700;
}

.viewer-time,
.viewer-size {
  font-size: 0.8rem;
  opacity: 0.8;
}

.viewer-size {
  margin-left: auto;
}

.viewer-count {
  font-size: 0.9rem;
}

.viewer-nav {
  position: absolute;
  top: 50%;
  z-index: 1;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 0;
  background: rgb(0 0 0 / 40%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.viewer-nav.prev {
  left: 8px;
}

.viewer-nav.next {
  right: 8px;
}

.viewer-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 0;
  background: rgb(255 255 255 / 14%);
  color: #fff;
  text-decoration: none;
  cursor: pointer;
}

.viewer-cant {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
}

.viewer-cant img {
  max-width: 60vw;
  max-height: 50vh;
}

/* ---- chat drawer ---- */

.drawer {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  flex-direction: column;
  background: var(--panel);
}

.drawer-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 8px calc(16px + env(safe-area-inset-bottom));
}

.tabs {
  display: flex;
  gap: 4px;
}

.tabs button {
  border: 0;
  background: none;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
}

.tabs button[aria-selected='true'] {
  background: var(--bg);
  color: var(--text);
}

.drawer-month {
  font-size: 0.85rem;
  color: var(--muted);
  font-weight: 600;
  margin: 14px 4px 6px;
}

.drawer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 2px;
}

.drawer-cell {
  position: relative;
  aspect-ratio: 1;
  border: 0;
  padding: 0;
  background: var(--line);
  overflow: hidden;
  cursor: pointer;
  color: var(--muted);
  display: flex;
  align-items: center;
  justify-content: center;
}

.drawer-cell img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.drawer-files {
  list-style: none;
  margin: 0;
  padding: 0;
}

.drawer-files li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 4px;
  border-bottom: 1px solid var(--line);
}

.drawer-files .file-card {
  max-width: none;
}

.drawer-from {
  font-size: 0.8rem;
  color: var(--muted);
  padding-left: 50px;
}

/* the drawer's links (design 01a 3) */
.drawer-links {
  list-style: none;
  margin: 0;
  padding: 0;
}

.drawer-links li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  border-bottom: 1px solid var(--line);
}

.drawer-link {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: inherit;
  text-decoration: none;
}

.drawer-link .icon {
  flex: none;
  margin-top: 2px;
  color: var(--muted);
}

.drawer-link-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.drawer-link-title {
  font-weight: 600;
  overflow: hidden;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.drawer-link-url {
  font-size: 0.8rem;
  color: var(--primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawer-link-from {
  font-size: 0.8rem;
  color: var(--muted);
}

.drawer-links button.link {
  flex: none;
  font-size: 0.85rem;
}

.empty-note {
  text-align: center;
  color: var(--muted);
  padding: 32px 0;
}

.drawer-sentinel {
  height: 1px;
}

/* ---- drive (design 7.2) ---- */

.drive {
  position: relative;
}

.drive-tabs {
  padding: 8px 12px;
  border-bottom: 1px solid var(--line);
}

.drive-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  position: relative;
  padding: 0 12px calc(24px + env(safe-area-inset-bottom));
}

.drive-head {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: 6px;
}

.drive-head .crumbs {
  flex: 1;
  min-width: 0;
}

.drive-head select {
  padding: 6px 8px;
  border-radius: 8px;
}

.link.with-icon {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.crumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 10px 0 4px;
  font-size: 0.95rem;
}

.crumbs .link {
  padding: 2px 4px;
}

.crumb-current {
  font-weight: 700;
  padding: 2px 4px;
}

.drive-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 6px 0 10px;
  background: var(--panel);
}

.drive-toolbar > button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.drive-items {
  list-style: none;
  margin: 0;
  padding: 0;
}

.drive-item {
  position: relative;
  display: flex;
  align-items: center;
}

.drive-items.list .drive-item {
  border-bottom: 1px solid var(--line);
}

.drive-open {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border: 0;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
  border-radius: 10px;
}

.drive-item.selected .drive-open {
  background: color-mix(in srgb, var(--primary) 14%, transparent);
}

.drive-thumb {
  position: relative;
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg);
  color: var(--primary);
}

.drive-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.drive-thumb .file-icon {
  width: 100%;
  height: 100%;
  border-radius: 0;
}

.drive-thumb .media-badge {
  left: 3px;
  bottom: 3px;
  padding: 0 5px;
  font-size: 0.65rem;
}

.drive-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.drive-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drive-meta {
  font-size: 0.8rem;
  color: var(--muted);
}

.drive-check {
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-text);
}

.drive-item.selected .drive-check {
  background: var(--primary);
}

.drive-items.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  gap: 8px;
}

.drive-items.grid .drive-open {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  padding: 4px;
}

.drive-items.grid .drive-thumb {
  width: 100%;
  height: auto;
  aspect-ratio: 1;
}

.drive-items.grid .drive-name {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-align: center;
  font-size: 0.85rem;
  word-break: break-all;
}

.drive-items.grid .drive-meta {
  display: none;
}

.drive-items.grid .drive-more {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 32px;
  height: 32px;
  background: rgb(255 255 255 / 80%);
  color: #1d2733;
}

.drive-items.grid .drive-check {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgb(255 255 255 / 80%);
}

.drive-items.grid .drive-item.selected .drive-check {
  background: var(--primary);
}

.drive-uploads {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
}

.drive-uploads li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 0.9rem;
}

.drive-up-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drive-uploads progress {
  width: 30%;
}

.trash-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  padding-right: 4px;
}

.trash-note {
  margin: 0 0 8px;
}

.pick-list {
  list-style: none;
  margin: 8px 0;
  padding: 0;
  max-height: 45dvh;
  overflow-y: auto;
}

.pick-list li > button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 8px;
  border: 0;
  background: none;
  color: inherit;
  text-align: left;
  border-radius: 10px;
  cursor: pointer;
}

.pick-list li > button:hover {
  background: var(--bg);
}

.pick-list li > button:disabled {
  opacity: 0.4;
  cursor: default;
}

/* ---- toast ---- */

.toast-area {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(88px + env(safe-area-inset-bottom));
  z-index: 40;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.toast {
  max-width: calc(100% - 32px);
  padding: 10px 18px;
  border-radius: 999px;
  background: rgb(29 39 51 / 92%);
  color: #fff;
  font-size: 0.9rem;
  box-shadow: var(--shadow);
  animation: toast-in 0.15s ease-out;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

/* ---- shared from another app ---- */

.share-page {
  gap: 12px;
}

.share-text {
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--bg);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.share-drive {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.share-title {
  margin: 8px 0 0;
  font-size: 1rem;
}

.rooms-pick {
  max-height: none;
}

/* ---- shared cards (design 01c) ---- */

.room-tabs {
  padding: 6px 12px;
  border-bottom: 1px solid var(--line);
  background: var(--panel);
}

/* the chat, and on a wide PC screen the board to its right (design 01b 9) */
.room-body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.room-chat {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.board-side {
  flex: none;
  width: 380px;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--line);
  background: var(--bg);
}

.icon-btn[aria-pressed='true'] {
  color: var(--primary);
  background: color-mix(in srgb, var(--primary) 14%, transparent);
}

/* a card looks the same whoever sent it (beats .msg.mine .bubble) */
.msg .bubble.card-bubble,
.msg.mine .bubble.card-bubble {
  width: 300px;
  max-width: 100%;
  padding: 12px;
  background: var(--panel);
  color: var(--text);
  border: 1px solid var(--line);
  white-space: normal;
}

.card-msg {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.card-kind {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--primary);
}

.card-msg.purchase .card-kind {
  color: var(--ok);
}

.card-title {
  font-size: 1.05rem;
  font-weight: 700;
}

.card-when {
  font-weight: 600;
}

.card-line {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.9rem;
}

.card-line .icon {
  flex: none;
  margin-top: 2px;
}

.card-note {
  margin: 2px 0 0;
  padding: 8px 10px;
  border-radius: 10px;
  background: var(--bg);
  font-size: 0.9rem;
  white-space: pre-wrap;
}

.d-day,
.card-progress {
  margin-left: auto;
  flex: none;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: var(--bg);
}

.d-day {
  color: var(--primary);
}

.card-progress {
  color: var(--muted);
}

.rsvp-list {
  margin: 2px 0 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.9rem;
}

.rsvp-list div {
  display: flex;
  gap: 8px;
}

.rsvp-list dt {
  flex: none;
  font-weight: 700;
}

.rsvp-list dd {
  margin: 0;
}

.rsvp-bar {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 4px;
}

.rsvp {
  min-height: 40px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel);
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
}

.rsvp[aria-pressed='true'] {
  background: var(--primary);
  border-color: var(--primary);
  color: var(--primary-text);
}

.rsvp-count {
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.8;
}

.card-msg .card-open {
  margin-top: 2px;
  padding: 8px 0 0;
  border-top: 1px solid var(--line);
  border-radius: 0;
}

/* "tensor에서 열기" (design 00 7): a link to the other app */
.card-msg a.card-open {
  display: block;
  text-align: center;
  text-decoration: none;
  color: var(--primary);
  font-weight: 600;
}

/* polls (design 01a 6) */

.card-msg.poll .card-kind {
  color: var(--unread-count);
}

.poll-tag {
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--muted);
  background: var(--bg);
}

.card-msg.poll.closed .d-day {
  color: var(--muted);
}

.poll-options {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.poll-row,
.poll-choice {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 42px;
  padding: 8px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel);
  color: inherit;
  font: inherit;
  text-align: left;
}

.poll-choice {
  cursor: pointer;
}

.poll-choice[aria-checked='true'] {
  border-color: var(--primary);
}

/* the share of the votes behind the text */
.poll-bar {
  position: absolute;
  inset: 0 auto 0 0;
  background: color-mix(in srgb, var(--primary) 12%, transparent);
  transition: width 0.25s;
}

.poll-option.mine .poll-bar,
.card-msg.poll.closed .poll-option.leader .poll-bar {
  background: color-mix(in srgb, var(--primary) 24%, transparent);
}

.card-msg.poll.closed .poll-option:not(.leader) .poll-bar {
  background: color-mix(in srgb, var(--muted) 14%, transparent);
}

.poll-mark,
.poll-text,
.poll-count {
  position: relative;
}

.poll-mark {
  flex: none;
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border: 2px solid var(--muted);
  border-radius: 50%;
}

.poll-choice[role='checkbox'] .poll-mark {
  border-radius: 5px;
}

.poll-mark.on {
  border-color: var(--primary);
  background: var(--primary);
  box-shadow: inset 0 0 0 3px var(--panel);
}

.poll-mark.none {
  display: none;
}

.poll-mark.mine {
  border: 0;
  color: var(--primary);
  font-weight: 800;
}

.poll-mark.mine::before {
  content: '✓';
}

.poll-text {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.poll-option.leader .poll-text {
  font-weight: 700;
}

.poll-count {
  flex: none;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--muted);
}

.poll-who {
  display: block;
  margin: 2px 4px 0;
  font-size: 0.8rem;
  color: var(--muted);
}

.poll-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.poll-actions .primary,
.poll-actions .secondary {
  min-height: 40px;
  padding: 0 16px;
}

.poll-inputs {
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 6px;
}

.poll-input {
  display: flex;
  align-items: center;
  gap: 4px;
}

.poll-input input {
  flex: 1;
  min-width: 0;
}

.poll-inputs > .link {
  align-self: flex-start;
}

/* "결과 보기" in a line from the server */
.system-link {
  margin-left: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

/* purchase lists */

.items {
  list-style: none;
  margin: 0;
  padding: 0;
}

.item-row {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid var(--line);
}

.item-row input[type='checkbox'] {
  flex: none;
  width: 22px;
  height: 22px;
  margin: 0 2px;
  accent-color: var(--primary);
  cursor: pointer;
}

.item-main {
  flex: 1;
  min-width: 0;
  min-height: 42px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
  padding: 6px 0;
}

button.item-main {
  border: 0;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.item-qty,
.item-meta {
  color: var(--muted);
}

.item-meta {
  font-size: 0.8rem;
}

.item-row.bought .item-name {
  color: var(--muted);
  text-decoration: line-through;
}

.item-row.pending {
  opacity: 0.6;
}

.add-item {
  display: flex;
  gap: 6px;
}

.add-item input {
  flex: 1;
  min-height: 40px;
  padding: 8px 10px;
}

.add-item button.secondary {
  min-height: 40px;
  padding: 0 14px;
}

.card-totals {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 2px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* the count and price under an item (a cart in full): − 3 + × 1500  4,500원 */
.item-row:has(.item-controls) {
  flex-wrap: wrap;
}

.item-controls {
  flex-basis: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 0 0 8px 34px;
  font-variant-numeric: tabular-nums;
}

.stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
}

.stepper button {
  width: 34px;
  height: 34px;
  border: 0;
  background: var(--bg);
  color: var(--text);
  font-size: 1.1rem;
  cursor: pointer;
}

.stepper button:disabled {
  color: var(--muted);
  cursor: default;
}

.item-controls .stepper input {
  width: 3.2em;
  height: 34px;
  padding: 0 4px;
  border: 0;
  border-radius: 0;
  text-align: center;
  background: var(--panel);
}

.item-times {
  color: var(--muted);
}

.item-controls input.item-price {
  width: 7em;
  min-height: 34px;
  padding: 4px 8px;
  text-align: right;
}

.item-controls button.link {
  width: auto;
  min-height: 0;
  padding: 0;
  font-size: 0.85rem;
}

.item-line-total {
  margin-left: auto;
  font-weight: 600;
}

.item-sheet-total {
  margin: 0;
  text-align: right;
}

/* a candidate's page (a big purchase) */
.item-link {
  flex: none;
  color: var(--primary);
}

.price-order {
  align-self: flex-start;
}

/* the cart's table (design 01c 3.3): rows typed like a spreadsheet */
.cart-view-tabs {
  align-self: flex-start;
}

.cart-table-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

/* wide for a phone: the table scrolls sideways, not the page */
.cart-table-scroll {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

.cart-table {
  width: 100%;
  min-width: 460px;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
  font-size: 0.95rem;
}

.cart-table th {
  text-align: left;
  font-weight: 600;
  font-size: 0.8rem;
  color: var(--muted);
  padding: 4px 6px;
  border-bottom: 1px solid var(--line);
  white-space: nowrap;
}

.cart-table td {
  border-bottom: 1px solid var(--line);
  padding: 0;
  vertical-align: middle;
}

.cart-table .num {
  text-align: right;
}

.cart-table .cell textarea {
  display: block;
  width: 100%;
  min-height: 38px;
  height: 38px;
  padding: 8px 6px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--text);
  font: inherit;
  resize: none;
  overflow: hidden;
  white-space: pre;
}

.cart-table .cell textarea:focus,
.cart-table .cell textarea:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
  background: var(--panel);
}

.cart-table .cell-name {
  min-width: 9em;
}

.cart-table .cell-count {
  width: 4.2em;
}

.cart-table .cell-price {
  width: 7.5em;
}

.cart-table .cell-count textarea,
.cart-table .cell-price textarea {
  text-align: right;
}

.cart-table .line-total {
  padding: 0 6px;
  width: 7em;
  white-space: nowrap;
  font-weight: 600;
}

.cart-table .col-check {
  width: 32px;
  text-align: center;
}

.cart-table .col-who select {
  min-height: 34px;
  padding: 4px 6px;
  border-radius: 8px;
  max-width: 6.5em;
}

.cart-table .col-del {
  width: 36px;
  text-align: center;
}

.cart-table tr.bought .cell-name textarea {
  text-decoration: line-through;
  color: var(--muted);
}

.cart-table tr.pending {
  opacity: 0.7;
}

.cart-table .draft-row textarea::placeholder {
  color: var(--muted);
}

.cart-table tfoot td,
.cart-table tfoot th {
  border-bottom: 0;
  padding: 8px 6px;
  font-weight: 600;
  color: var(--text);
  font-size: 0.95rem;
}

.cart-table-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.cart-table-bar button.secondary {
  min-height: 40px;
  padding: 0 14px;
}

/* "장 보러 가요" and who went (design 01c 3) */
.going-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
}

.card-msg .going-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  width: auto;
  min-height: 32px;
  padding: 0;
  font-size: 0.85rem;
}

.going-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 6px 10px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--primary) 12%, transparent);
  font-size: 0.85rem;
  font-weight: 600;
}

.going-line .grow {
  flex: 1;
  min-width: 0;
}

.going-line button.link {
  width: auto;
  min-height: 0;
  padding: 0;
}

/* the room's frequent items when making a list */
.frequent-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* what the room spent, month by month */
.spending {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
}

.spending-now {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.spending-now .grow {
  flex: 1;
  font-variant-numeric: tabular-nums;
}

.spending-months {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.spending-months li {
  display: grid;
  grid-template-columns: 6.5em 1fr auto;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}

.spending-track {
  height: 8px;
  border-radius: 4px;
  background: var(--bg);
  overflow: hidden;
}

.spending-bar {
  display: block;
  height: 100%;
  border-radius: 4px;
  background: var(--primary);
}

.spending p {
  margin: 0;
}

/* the detail sheet and the forms */

.card-sheet {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.receipt {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.receipt-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  font-weight: 600;
}

.receipt-link img {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 8px;
}

.menu.card-form {
  gap: 12px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 8px;
}

.choice {
  margin: 0;
  padding: 0;
  border: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 2px 16px;
}

.choice legend {
  padding: 0;
  margin-bottom: 4px;
  font-size: 0.9rem;
  font-weight: 600;
}

.card-form .check {
  min-height: 36px;
}

.card-form .check input {
  width: 20px;
  height: 20px;
  margin: 0;
}

/* a room's events and lists */

.room-cards {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px calc(16px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.room-cards > * {
  width: 100%;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
}

.new-card {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.pick-list.card-list {
  margin: 0;
  max-height: none;
  overflow: visible;
}

.card-list li + li {
  border-top: 1px solid var(--line);
}

.pick-list li > button.card-row {
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
}

.card-row-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.card-row-meta {
  font-size: 0.85rem;
  color: var(--muted);
}

.card-list.past {
  opacity: 0.7;
}

.room-cards .past-toggle {
  width: auto;
  align-self: center;
}

/* the tensor tab (design 01c 3.4): the month, its sums, 수입 · 지출 · 쇼핑 */

.tensor-month {
  justify-content: center;
}

.tensor-totals dl {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
}

.tensor-total {
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
  min-width: 0;
}

.tensor-total dt {
  font-size: 0.8rem;
  color: var(--muted);
  font-weight: 600;
}

.tensor-total dd {
  margin: 2px 0 0;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

.tensor-total.income dd {
  color: var(--ok);
}

.tensor-total.balance {
  background: color-mix(in srgb, var(--primary) 10%, var(--panel));
}

.tensor-total.balance.minus dd {
  color: var(--danger);
}

@media (max-width: 520px) {
  .tensor-totals dl {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.tensor-tabs {
  border-bottom: 1px solid var(--line);
  padding-bottom: 6px;
}

/* the room's workbook of the tensor app and tensor cards (design 02 12.3) */

.tensor-link {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
}

.tensor-link-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.tensor-link-row .grow {
  flex: 1;
  min-width: 10em;
  display: flex;
  align-items: center;
  gap: 4px;
}

.tensor-link p {
  margin: 0;
}

.tensor-books {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tensor-books button {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
}

.tensor-books button .grow {
  flex: 1;
}

.tensor-views {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 0;
  margin: 0;
  padding: 0;
}

.card-msg.tensor .card-kind {
  color: #6a4fb6;
}

.tensor-bars {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tensor-bars li {
  display: grid;
  grid-template-columns: minmax(4em, 7em) 1fr auto;
  align-items: center;
  gap: 2px 8px;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}

.tensor-bar-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tensor-bar-note {
  grid-column: 2 / 4;
  font-size: 0.75rem;
  color: var(--muted);
}

.tensor-bars li.over .spending-bar {
  background: var(--danger);
}

.tensor-bars li.over .tensor-bar-note {
  color: var(--danger);
}

.ledger,
.shopping,
.cart-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ledger-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
}

.pick-list li > button.ledger-row {
  display: grid;
  grid-template-columns: 5.5em minmax(0, 1fr) auto;
  align-items: center;
  gap: 2px 10px;
}

.ledger-day {
  color: var(--muted);
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}

.ledger-item {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.ledger-amount {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.ledger-meta {
  grid-column: 2 / -1;
  font-size: 0.85rem;
  color: var(--muted);
  overflow-wrap: anywhere;
}

.ledger-sum,
.cart-section-sum {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 2px 12px;
  margin: 0;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.cart-section-sum .grow {
  flex: 1;
}

.shopping-sums {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 12px;
}

.shopping-sums th,
.shopping-sums td {
  padding: 6px 10px;
  text-align: right;
  font-size: 0.9rem;
}

.shopping-sums th[scope='row'] {
  text-align: left;
  font-weight: 600;
}

.shopping-sums thead th {
  color: var(--muted);
  font-size: 0.8rem;
}

.shopping-sums tfoot th,
.shopping-sums tfoot td {
  border-top: 1px solid var(--line);
  font-weight: 700;
}

.cart-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
}

.cart-block .card-open {
  align-self: flex-start;
  width: auto;
}

/* the cart screens (design 01c 3.5~3.9, Carts.tsx): a cart's number before
   its title, 구매 완료, deleted carts brought back, the room's 카트 tab and
   the chat list's /carts */

.cart-no {
  display: inline-block;
  margin-right: 6px;
  padding: 1px 6px;
  border-radius: 6px;
  background: var(--bg);
  color: var(--muted);
  font-size: 0.78rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  vertical-align: 0.1em;
}

/* a cart without a title: its number is its name */
.cart-no:only-child {
  margin: 0;
  padding: 0;
  background: none;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  vertical-align: baseline;
}

.tag.bought-tag {
  color: var(--ok);
  font-weight: 700;
}

/* a narrow bubble: the kind, its tags and the count go to a second line
   whole (Korean would otherwise break inside "카트" and the tags) */
.card-msg.purchase .card-kind {
  flex-wrap: wrap;
  row-gap: 4px;
  white-space: nowrap;
}

.done-prompt {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--ok);
}

.link.done-prompt {
  align-self: flex-start;
  padding: 4px 0;
}

.bought-part {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
}

.bought-part.done {
  border-color: var(--ok);
}

.bought-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-weight: 600;
}

.bought-part.done .bought-line {
  color: var(--ok);
}

.bought-part .check input {
  width: 20px;
  height: 20px;
  margin: 0;
}

.bought-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}

.card-room {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}

.deleted-cart {
  gap: 8px;
}

.deleted-items {
  margin: 0;
  padding-left: 20px;
  color: var(--muted);
}

.restore-note {
  margin: 0;
  font-weight: 600;
}

.cart-trash-part {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--line);
}

.cart-trash-part .past-toggle {
  align-self: center;
  width: auto;
}

.cart-trash-part p {
  margin: 0;
}

.cart-trash {
  list-style: none;
  margin: 0;
  padding: 0;
}

.trash-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
}

.trash-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.trash-main .card-row-title {
  display: block;
  overflow-wrap: anywhere;
}

.trash-row > button {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 0 12px;
}

.card-row-title .grow {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

/* the page scrolls; what is on it keeps its height (the tabs scroll
   sideways, which would otherwise let them shrink to nothing) */
.carts-body > * {
  flex-shrink: 0;
}

.carts-tabs {
  border-bottom: 1px solid var(--line);
  padding-bottom: 6px;
  overflow-x: auto;
  scrollbar-width: none;
}

.carts-tabs button {
  flex: none;
}

.carts-body > .new-card {
  align-self: flex-start;
}

.carts-room {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.carts-room-name {
  margin: 4px 0 0;
  font-size: 0.95rem;
}

.carts-room-name .link {
  padding: 2px 0;
  color: inherit;
  font-weight: 700;
  text-align: left;
}

.carts-body > p {
  margin: 0;
}

/* six room tabs on a 360 px phone: tighter, sharing the width (they still
   scroll inside the bar rather than the page if a name is long) */
@media (max-width: 480px) {
  .room-tabs.tabs {
    gap: 0;
    padding: 6px 4px;
  }

  .room-tabs.tabs button {
    flex: 1 0 auto;
    padding: 8px 6px;
    font-size: 0.9rem;
  }

  .carts-tabs.tabs {
    gap: 0;
  }

  .carts-tabs.tabs button {
    padding: 8px 9px;
  }
}

/* the family calendar */

.cal-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cal-nav h3 {
  margin: 0;
  min-width: 7.5em;
  text-align: center;
  font-size: 1.1rem;
}

.cal-nav button.secondary {
  margin-left: auto;
  min-height: 36px;
  padding: 0 14px;
}

.cal-grid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.cal-grid th {
  padding: 4px 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--muted);
}

.cal-grid th:first-child {
  color: var(--danger);
}

.cal-grid th:last-child {
  color: var(--primary);
}

.cal-grid td {
  padding: 1px;
}

.cal-grid button {
  width: 100%;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 4px 0;
  border: 0;
  border-radius: 10px;
  background: none;
  color: inherit;
  cursor: pointer;
}

.cal-grid button:hover {
  background: var(--bg);
}

.cal-grid button.selected {
  background: var(--bg);
  box-shadow: inset 0 0 0 2px var(--primary);
}

.cal-grid button.other {
  opacity: 0.4;
}

.cal-num {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.cal-grid .sun .cal-num {
  color: var(--danger);
}

.cal-grid .sat .cal-num {
  color: var(--primary);
}

.cal-grid .today .cal-num {
  background: var(--primary);
  color: var(--primary-text);
}

.cal-dots {
  display: flex;
  gap: 3px;
}

.cal-dots i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--primary);
}

.cal-day h3 {
  margin: 0 0 4px;
  font-size: 1rem;
}

.cal-day .empty-note {
  padding: 16px 0;
}

.cal-time {
  margin-right: 2px;
  color: var(--primary);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

@media (min-width: 900px) {
  .cal-grid button {
    min-height: 64px;
  }
}

/* the calendar's views and "일정 추가"; the week (design 01c 2.1) */

.cal-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.cal-bar .new-card {
  margin-left: auto;
  width: auto;
}

.room-calendar .cal-day h3,
.room-calendar .cal-nav h3 {
  font-size: 1rem;
}

.room-calendar .past {
  opacity: 0.7;
}

.cal-week-title {
  flex: 1;
  min-width: 0;
}

.cal-week {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cal-week-day {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--line);
}

.cal-week-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 6px;
  border: 0;
  border-radius: 10px;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.cal-week-head[aria-pressed='true'] {
  background: var(--bg);
  box-shadow: inset 0 0 0 2px var(--primary);
}

.cal-week-num {
  font-size: 1.1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.cal-week-wd {
  font-size: 0.85rem;
  color: var(--muted);
}

.cal-week-head.sun .cal-week-num {
  color: var(--danger);
}

.cal-week-head.sat .cal-week-num {
  color: var(--primary);
}

.cal-week-day.today .cal-week-wd {
  color: var(--primary);
  font-weight: 700;
}

.cal-week-none {
  margin: 0;
  padding: 0 6px;
}

.family-tag {
  color: var(--primary);
}

.cal-room {
  font-weight: 600;
}

/* an event's place picked on the map (design 01c 2) */

.event-pin {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.event-pin .link {
  width: auto;
}

/* moving a cart's items (design 01c 3): ↑ ↓, and a PC's drag handle */

.item-move {
  flex: none;
  display: inline-flex;
}

.item-grip {
  flex: none;
  display: inline-flex;
  color: var(--muted);
  cursor: grab;
}

.item-row.dragging {
  opacity: 0.5;
}

.item-row.drag-over {
  box-shadow: inset 0 2px 0 var(--primary);
}

/* ---- the board (design 01b) ---- */

.tab-badge {
  margin-left: 6px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--primary);
  color: var(--primary-text);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.board {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px calc(16px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.board > * {
  width: 100%;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
}

.board-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.board-bar button.secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}

.slot {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
  min-width: 0;
}

.slots.done .slot {
  background: var(--bg);
}

.slot.finished .slot-body {
  opacity: 0.55;
}

.slot-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
}

.slot-kind {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--primary);
}

.slot.gifticon .slot-kind {
  color: var(--unread-count);
}

.icon-btn.small {
  width: 32px;
  height: 32px;
}

.slot-body {
  position: relative;
}

.slot-media {
  display: grid;
  gap: 3px;
  border-radius: 10px;
  overflow: hidden;
}

.slot-media.n2,
.slot-media.n4 {
  grid-template-columns: 1fr 1fr;
}

.slot-media.n3 {
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.slot-media.n3 .slot-thumb:first-child {
  grid-row: span 2;
}

.slot-thumb {
  position: relative;
  border: 0;
  padding: 0;
  background: var(--line);
  color: var(--muted);
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
}

.slot-media.n1 .slot-thumb {
  aspect-ratio: auto;
  max-height: 260px;
}

.slot.gifticon .slot-media.n1 .slot-thumb {
  max-height: 160px;
}

.slot-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slot-media.n1 .slot-thumb img {
  object-fit: contain;
  max-height: 260px;
}

.slot-more {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(0 0 0 / 45%);
  color: #fff;
  font-weight: 700;
  font-size: 1.1rem;
}

.slot-file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 10px;
  background: var(--bg);
  color: inherit;
  text-decoration: none;
  min-width: 0;
}

.slot-file + .slot-file,
.slot-media + .slot-file {
  margin-top: 4px;
}

.slot-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.slot-cards {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* cards with the photos and files of a slot (design 01b 2.2) */
.slot-media + .slot-cards,
.slot-file + .slot-cards {
  margin-top: 6px;
}

.slot-card-ref {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border: 0;
  border-radius: 10px;
  background: var(--bg);
  color: inherit;
  text-align: left;
  font-weight: 600;
  cursor: pointer;
}

.slot-note {
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--mine);
  color: var(--mine-text);
  white-space: pre-wrap;
  max-height: 9.5em;
  overflow: hidden;
}

.slot-sheet .slot-note {
  max-height: none;
}

/* "사용함" and the like, stamped over what the slot shows */
.stamp {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-12deg);
  padding: 2px 14px;
  border: 3px solid var(--danger);
  border-radius: 8px;
  color: var(--danger);
  background: var(--stamp-bg);
  font-weight: 800;
  font-size: 1.3rem;
  letter-spacing: 0.1em;
  pointer-events: none;
  /* the mark's icon by its word (design 01b 3) */
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.stamp.done {
  border-color: var(--ok);
  color: var(--ok);
}

.stamp.expired {
  border-color: var(--muted);
  color: var(--muted);
}

.slot-title {
  margin: 0;
  font-size: 1.05rem;
}

.slot-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slot-title-row .slot-title {
  flex: 1;
  min-width: 0;
}

/* what the messages under a slot say (design 01b 2.3) */
.slot-chips {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 6px;
}

.slot-chips .fact {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--bg);
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

.slot-chips .fact .icon {
  flex: none;
  color: var(--muted);
}

/* a date makes an event (design 01c 2) */
.slot-chips .fact-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--primary);
  font: inherit;
  text-decoration: underline dotted;
  cursor: pointer;
}

.slots.done .slot-chips .fact {
  background: var(--panel);
}

.slot-entry-why {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--muted);
  font-size: 0.75rem;
}

.slot-keywords {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
}

.slot-keywords .tag {
  margin-left: 0;
}

.keyword-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.keyword-list li {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding-left: 10px;
  border: 1px solid var(--line);
  border-radius: 999px;
}

.keyword-list .icon-btn.small {
  width: 28px;
  height: 28px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.slot-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  font-size: 0.9rem;
}

.d-day.soon {
  color: var(--soon-text);
  background: var(--soon-bg);
}

.slot-acks {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.85rem;
}

.slot-acks-text {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

/* who has seen it: their faces, overlapping, then "+n" (design 01b 3.1) */
.slot-acks-who {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  min-width: 0;
}

.ack-faces {
  display: inline-flex;
  align-items: center;
  flex: none;
}

.ack-face,
.ack-more {
  display: inline-flex;
  border-radius: 38%;
  box-shadow: 0 0 0 2px var(--panel);
}

.ack-face + .ack-face,
.ack-face + .ack-more {
  margin-left: -6px;
}

.ack-more {
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--line);
  font-size: 0.7rem;
  font-weight: 700;
}

.slots.done .ack-face,
.slots.done .ack-more {
  box-shadow: 0 0 0 2px var(--bg);
}

.bubble.sticker {
  background: none;
  padding: 0;
  box-shadow: none;
}

.sticker-img {
  display: block;
  width: 140px;
  height: 140px;
  object-fit: contain;
}

.sticker-tabs {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.sticker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(76px, 1fr));
  gap: 6px;
  margin-bottom: 12px;
}

.sticker-grid.small {
  grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
}

.sticker-cell {
  position: relative;
  aspect-ratio: 1;
  padding: 4px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel);
  cursor: pointer;
}

.sticker-cell img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sticker-cell .icon-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 24px;
  min-height: 24px;
  padding: 2px;
  border-radius: 50%;
  background: var(--panel);
  box-shadow: var(--shadow);
}

.sticker-new {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.sticker-set-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sticker-set-head h4 {
  margin: 8px 0;
}

.bookmarked {
  color: var(--primary);
  display: inline-flex;
}

.bookmarks h3 {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 16px 0 8px;
  font-size: 0.95rem;
}

.typing-line {
  margin: 0;
  padding: 2px 14px;
  color: var(--muted);
  font-size: 0.8rem;
}

.scheduled-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 12px;
  border: 0;
  border-top: 1px solid var(--line);
  background: var(--panel);
  color: var(--muted);
  font-size: 0.85rem;
  text-align: left;
  cursor: pointer;
}

.scheduled-bar.failed {
  color: var(--danger);
}

.scheduled-text {
  white-space: pre-wrap;
}

.schedule-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.schedule-form textarea {
  width: 100%;
  resize: vertical;
}

.schedule-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.schedule-when {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  flex: none;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--panel);
  font-weight: 600;
  cursor: pointer;
}

.chip.on {
  background: var(--primary);
  border-color: var(--primary);
  color: var(--primary-text);
}

.slot-entries {
  list-style: none;
  margin: 0;
  padding: 6px 0 0;
  border-top: 1px dashed var(--line);
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.85rem;
}

.slot-entry {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 6px;
}

.slot-entry-who {
  font-weight: 700;
}

.slot-entry-time {
  color: var(--muted);
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
}

.slot-entry-text {
  flex: 1 1 100%;
  white-space: pre-wrap;
}

.slot-entry-text.deleted {
  color: var(--muted);
  font-style: italic;
}

.slot-entry .tag {
  margin-left: 0;
}

.slot-entry .icon-btn.small {
  width: 28px;
  height: 28px;
  margin-left: auto;
}

.slot-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.slot-actions button {
  flex: 1 1 auto;
  min-height: 40px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.slot-actions button[aria-pressed='true'] {
  background: var(--primary);
  color: var(--primary-text);
}

.taken-down .link {
  padding-left: 0;
}

.pick-list.down-list {
  max-height: none;
  overflow: visible;
}

.down-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 8px;
}

.down-title {
  font-weight: 600;
}

.down-actions {
  display: flex;
  gap: 4px;
}

.down-actions .link {
  padding-left: 0;
}

.slot-sheet {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.slot-sheet .slot {
  border: 0;
  padding: 0;
}

.slot-write {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.slot-write input {
  flex: 1;
}

/* a message written in the slot: Korean input as in the chat (design 8.7) */
.slot-write textarea {
  flex: 1;
  resize: none;
  line-height: 1.4;
}

/* what a slot of photos, files and cards shows, each to take off, and
   "더 붙이기" (design 01b 2.2) */
.slot-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.slot-items-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.slot-items-head button.secondary,
.pick-part > button.secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 40px;
  padding: 0 12px;
}

.slot-item-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slot-item-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
}

.slot-item-icon {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg);
  color: var(--muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.slot-item-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slot-item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* photos and files with cards, picked together for one slot */
.pick-part {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pick-head {
  margin: 0;
  font-size: 0.9rem;
}

.pick-cards {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 35dvh;
  overflow-y: auto;
}

.pick-card .icon {
  flex: none;
  color: var(--muted);
}

/* a message written under a slot */
.board-mark {
  border: 0;
  padding: 2px;
  background: none;
  color: var(--primary);
  cursor: pointer;
  display: inline-flex;
}

/* the gifticon full screen: dark, bright picture (design 01b 4.2) */
.barcode {
  position: fixed;
  inset: 0;
  z-index: 35;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: calc(12px + env(safe-area-inset-top)) 16px calc(16px + env(safe-area-inset-bottom));
  background: #000;
  color: #fff;
}

.barcode-top {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.barcode-top .icon-btn {
  color: #fff;
}

.barcode-title {
  font-weight: 700;
}

.barcode-image {
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.barcode-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  background: #fff;
}

.barcode-hint {
  margin: 0;
  color: #ccc;
  font-size: 0.9rem;
}

.barcode button.primary {
  width: 100%;
  max-width: 420px;
}

.gift-pick {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gift-pick img {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 10px;
}

.gift-pick button.secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pick-grid {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 4px;
  max-height: 50dvh;
  overflow-y: auto;
}

.pick-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg);
  cursor: pointer;
}

.pick-cell.on {
  outline: 3px solid var(--primary);
  outline-offset: -3px;
}

.pick-cell input {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 20px;
  height: 20px;
  margin: 0;
  z-index: 1;
}

.pick-cell img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pick-name {
  padding: 4px;
  font-size: 0.75rem;
  text-align: center;
  overflow-wrap: anywhere;
}

/* the server's health on the admin screen (operations guide 8) */
.health-problems {
  margin: 0 0 8px;
  padding-left: 1.2em;
  color: var(--danger);
  font-weight: 600;
}

.health-facts {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  margin: 0;
  font-size: 0.9rem;
}

.health-facts dt {
  color: var(--muted);
}

.health-facts dd {
  margin: 0;
  overflow-wrap: anywhere;
}

/* the server's log under it: fixed width, the newest line at the bottom */
.log-tools select {
  flex: 1;
  min-width: 0;
}

.log-lines {
  max-height: 60vh;
  overflow: auto;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--bg);
  font-family: ui-monospace, 'Cascadia Mono', Consolas, D2Coding, monospace;
  font-size: 0.78rem;
  line-height: 1.4;
}

.log-line {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.log-time,
.log-line.debug {
  color: var(--muted);
}

.log-line.warn .log-level {
  color: var(--unread-count);
  font-weight: 700;
}

.log-line.error {
  color: var(--danger);
}

/* KakaoTalk's own browser: off to the phone's browser (ui/InApp.tsx) */
.inapp-guide {
  justify-content: center;
}

.inapp-guide h2,
.inapp-guide p {
  margin: 0;
}

.inapp-guide h2 {
  font-size: 1.2rem;
}

/* the board's rules (design 01b 10) */
.rule-stats table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  font-variant-numeric: tabular-nums;
}

.rule-stats th,
.rule-stats td {
  padding: 4px 6px;
  text-align: right;
  border-bottom: 1px solid var(--line);
}

.rule-stats thead th {
  color: var(--muted);
  font-weight: 600;
}

.rule-stats th:first-child {
  text-align: left;
}

.rule-stats tbody th {
  font-weight: 500;
}

.rule-stats tr.sub th {
  padding-left: 20px;
  color: var(--muted);
}

.rule-stats tr.often td:last-child {
  color: var(--danger);
  font-weight: 600;
}

/* message search (design 01a 8) */
.search-bar {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 4px;
  padding: 0 14px;
  border-radius: 999px;
  background: var(--bg);
  color: var(--muted);
}

.search-bar:focus-within {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}

.search-bar .icon {
  flex: none;
}

.search-bar input {
  flex: 1;
  min-width: 0;
  min-height: 40px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--text);
  font: inherit;
}

.search-bar input:focus {
  outline: none;
}

.search-scope {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 8px;
  padding: 6px 16px;
  border-bottom: 1px solid var(--line);
  font-size: 0.9rem;
  color: var(--muted);
}

.search-results {
  list-style: none;
  margin: 0;
  padding: 0;
}

.search-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid var(--line);
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.search-meta {
  display: flex;
  gap: 8px;
  min-width: 0;
  font-size: 0.82rem;
  color: var(--muted);
}

.search-room {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 700;
  color: var(--text);
}

.search-when {
  flex: none;
  margin-left: auto;
}

.search-text {
  overflow-wrap: anywhere;
}

mark {
  border-radius: 3px;
  padding: 0 1px;
  background: var(--mine);
  color: var(--mine-text);
}

/* the conversation around a found message */
.around {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.around-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 55vh;
  overflow-y: auto;
}

.around-day,
.around-system {
  margin: 6px 0 2px;
  text-align: center;
  font-size: 0.78rem;
  color: var(--muted);
}

.around-msg {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 10px;
  border-radius: 10px;
}

.around-msg.found {
  background: var(--bg);
  box-shadow: inset 3px 0 0 var(--primary);
}

.around-meta {
  font-size: 0.78rem;
  color: var(--muted);
}

.around-text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* ---- app lock (features/applock): over everything, the toast too ---- */

.app-lock {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: var(--bg);
}

.app-lock-box {
  width: 100%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
}

.app-lock-box h2 {
  margin: 0;
  font-size: 1.2rem;
}

.app-lock-icon {
  display: inline-flex;
  padding: 16px;
  border-radius: 50%;
  background: var(--panel);
  color: var(--primary);
  box-shadow: var(--shadow);
}

.app-lock-box form {
  width: 100%;
  display: flex;
  gap: 8px;
}

.app-lock-pin {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  font-size: 1.4rem;
  letter-spacing: 0.4em;
  text-align: center;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel);
}

/* ---- a room's background for me (ui/background.tsx, design 01a 5) ---- */

[data-bg='sky'] {
  --room-tint: #7fb8ff;
}

[data-bg='mint'] {
  --room-tint: #6fd6b5;
}

[data-bg='lemon'] {
  --room-tint: #ffd84d;
}

[data-bg='peach'] {
  --room-tint: #ffab7a;
}

[data-bg='rose'] {
  --room-tint: #f59bc0;
}

[data-bg='lavender'] {
  --room-tint: #b3a0f2;
}

[data-bg='sand'] {
  --room-tint: #d6bf9b;
}

[data-bg='slate'] {
  --room-tint: #8394aa;
}

.messages-wrap[data-bg]:not([data-bg='photo']) {
  background: color-mix(in srgb, var(--room-tint) var(--tint-share), var(--bg));
}

/* the photo under a veil, so the bubbles stay easy to read; the words
   outside the bubbles get a halo */
.messages-wrap[data-bg='photo'] {
  background:
    linear-gradient(var(--photo-veil), var(--photo-veil)),
    var(--room-photo) center / cover no-repeat,
    var(--chat-bg);
}

.messages-wrap[data-bg='photo'] :is(.msg-meta, .msg-sender, .room-start) {
  color: var(--text);
  text-shadow:
    0 0 3px var(--bg),
    0 0 6px var(--bg);
}

.bg-swatches {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 8px;
}

.bg-swatches .bg-swatch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 4px;
  border: 2px solid transparent;
  border-radius: 12px;
  background: none;
  font-size: 0.85rem;
}

.bg-swatches .bg-swatch[aria-pressed='true'] {
  border-color: var(--primary);
}

.bg-swatch-color {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--line);
  background: var(--chat-bg);
}

.bg-swatch[data-bg] .bg-swatch-color {
  background: color-mix(in srgb, var(--room-tint) var(--tint-share), var(--bg));
}

/* ---- calls (ui/CallScreen.tsx, design 01a 7.1): dark in both themes ---- */

.call-screen {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  flex-direction: column;
  background: #10151b;
  color: #f2f5f8;
}

.call-head {
  position: relative;
  padding: calc(12px + env(safe-area-inset-top)) 56px 8px;
  text-align: center;
}

.call-head .call-info-btn {
  position: absolute;
  top: calc(6px + env(safe-area-inset-top));
  right: 8px;
  color: #b8c3cf;
}

.call-head .call-info-btn[aria-pressed='true'] {
  color: #fff;
}

/* "통화 정보": over the tiles, under the controls */
.call-info {
  position: absolute;
  top: calc(64px + env(safe-area-inset-top));
  left: 8px;
  right: 8px;
  z-index: 2;
  max-width: 520px;
  max-height: calc(100% - 180px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  margin: 0 auto;
  overflow-y: auto;
  padding: 8px 12px 10px;
  border-radius: 12px;
  background: rgb(16 21 27 / 94%);
  border: 1px solid rgb(255 255 255 / 16%);
  font-size: 0.85rem;
}

.call-info-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.call-info-head h3 {
  margin: 0;
  font-size: 0.95rem;
}

.call-info-head .icon-btn {
  color: #b8c3cf;
}

.call-info-peer {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 3px 12px;
  margin: 6px 0 0;
  padding: 8px 0 0;
  border-top: 1px solid rgb(255 255 255 / 12%);
}

.call-info-peer dt {
  color: #8a99a8;
}

.call-info-peer dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.call-info p {
  margin: 8px 0 0;
  color: #b8c3cf;
}

/* a voice the browser did not play: one touch plays it (above the name,
   clear of "통화 정보" at the top) */
.call-sound {
  position: absolute;
  bottom: 48px;
  left: 50%;
  z-index: 1;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 0;
  border-radius: 999px;
  background: #f2f5f8;
  color: #10151b;
  font: inherit;
  font-weight: 600;
  white-space: nowrap;
}

.call-head h2 {
  margin: 0;
  font-size: 1.15rem;
}

.call-status {
  margin: 4px 0 0;
  color: #b8c3cf;
  font-size: 0.9rem;
}

.call-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 1fr;
  gap: 6px;
  padding: 6px;
}

.call-grid.n3,
.call-grid.n4 {
  grid-template-columns: 1fr 1fr;
}

@media (min-width: 700px) {
  .call-grid.n2 {
    grid-template-columns: 1fr 1fr;
  }
}

.call-tile {
  position: relative;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  overflow: hidden;
  background: #1c252f;
}

/* the picture only (the sound plays in the tile's <audio>): hidden while there is none */
.call-tile video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
}

.call-tile.with-video video {
  opacity: 1;
}

.call-tile-person {
  position: relative;
  color: #8a99a8;
}

.call-tile-name {
  position: absolute;
  left: 10px;
  bottom: 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgb(0 0 0 / 45%);
  font-size: 0.85rem;
}

.call-self {
  position: absolute;
  right: 12px;
  bottom: calc(96px + env(safe-area-inset-bottom));
  width: 28vw;
  max-width: 180px;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border: 2px solid rgb(255 255 255 / 30%);
  border-radius: 12px;
  background: #000;
}

.call-self.mirror {
  transform: scaleX(-1);
}

.call-controls {
  display: flex;
  justify-content: center;
  gap: 14px;
  padding: 14px 16px calc(18px + env(safe-area-inset-bottom));
}

.call-controls .icon-btn {
  width: 56px;
  height: 56px;
  background: rgb(255 255 255 / 14%);
  color: #fff;
}

.call-controls .icon-btn[aria-pressed='true'] {
  background: #f2f5f8;
  color: #10151b;
}

.call-controls .icon-btn.call-hangup {
  background: #e5484d;
  color: #fff;
}

.call-incoming {
  position: fixed;
  inset: 0;
  z-index: 95;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgb(10 14 19 / 94%);
  color: #fff;
}

.call-incoming-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}

.call-incoming-box h2 {
  margin: 6px 0 0;
  font-size: 1.5rem;
}

.call-incoming-box p {
  margin: 0;
  color: #c9d3dd;
}

.call-incoming-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}

.call-incoming-buttons button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 52px;
  padding: 0 22px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
}

.call-decline {
  background: #e5484d;
}

.call-accept {
  background: #2e9e5b;
}

.call-accept.by-voice {
  background: #3c5a78;
}

.call-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--line);
  background: var(--panel);
  color: var(--ok);
  font-weight: 600;
}

/* ---- location (design 01c 4, location.tsx) ---- */

/* five tabs fit a phone: they scroll sideways when they do not */
.room-tabs {
  overflow-x: auto;
  scrollbar-width: none;
}

.room-tabs button {
  flex: none;
}

.map-box {
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: var(--bg);
  border: 1px solid var(--line);
}

.map-mini {
  width: 100%;
  height: 140px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--bg);
  pointer-events: none;
}

.map-pick {
  height: 260px;
}

.map-tab {
  height: 45dvh;
  min-height: 240px;
}

.map-trails {
  height: 65dvh;
  min-height: 300px;
}

/* lines and circles on the map (features/map/shapes.ts): an SVG hanging
   from a box of no size, so the map's anchor cannot move it and nothing
   catches taps or drags meant for the map */
.map-shapes {
  position: relative;
  width: 0;
  height: 0;
}

.map-shapes svg {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  pointer-events: none;
}

/* a pin on the map: a name over a dot (CustomOverlay, built with textContent) */
.map-pin {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  transform: translateY(2px);
  pointer-events: none;
}

.map-pin-label,
.map-pin-sub {
  max-width: 160px;
  padding: 1px 6px;
  border-radius: 8px;
  background: var(--panel);
  color: var(--text);
  box-shadow: var(--shadow);
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.map-pin-sub {
  font-weight: 400;
  font-size: 0.72rem;
  color: var(--muted);
}

.map-pin-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--primary);
  border: 3px solid #fff;
  box-shadow: var(--shadow);
}

.card-msg.location .card-kind {
  color: #b4532a;
}

.card-msg.location.ended {
  opacity: 0.85;
}

.tag.live-tag {
  background: color-mix(in srgb, var(--ok) 16%, transparent);
  color: var(--ok);
}

.loc-where {
  overflow-wrap: anywhere;
}

.loc-summary {
  font-weight: 600;
}

.loc-summary.error {
  color: var(--danger);
}

.loc-note {
  margin: 0;
}

.loc-stop {
  align-self: flex-start;
}

.loc-links {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 0.85rem;
}

.loc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.loc-actions button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: auto;
}

.loc-durations {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.loc-people {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.live-list li .loc-links {
  padding: 0 8px 10px;
}

.radius-choice {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
}

.trails-page {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* the location helper (design 01c 4.4): what the phone last said */
.helper-checks {
  list-style: none;
  margin: 8px 0;
  padding: 0;
}

.helper-checks li {
  padding: 2px 0;
}

.helper-checks li.bad {
  color: var(--danger);
}

.helper-checks li.unknown {
  color: var(--muted);
}

.event-trail {
  margin-top: 8px;
}

.trail-interval {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0;
}

/* calendar carts, prices, location screen (design 01c 3.7, 3.8, 4.6) */

/* the chat list's header has seven buttons: smaller ones when the list is
   narrow (a 360 px phone, or the list beside a room on a PC, 300-360 px) */
.room-list-bar {
  container-type: inline-size;
  gap: 0;
  padding-right: 4px;
}

@media (max-width: 420px) {
  .room-list-bar .icon-btn {
    width: 36px;
    height: 40px;
    border-radius: 10px;
  }
}

@container (max-width: 400px) {
  .room-list-bar .icon-btn {
    width: 36px;
    height: 40px;
    border-radius: 10px;
  }
}

@container (max-width: 330px) {
  .room-list-bar .icon-btn {
    width: 32px;
  }

  .room-list-bar .icon-btn .icon {
    width: 20px;
    height: 20px;
  }
}

/* a bought cart in the calendar (3.7): a small square under the day, and
   a row after the day's events */
.cal-dots i.cart {
  border-radius: 1.5px;
  background: var(--unread-count);
}

.cal-cart-name {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  overflow-wrap: anywhere;
}

.cal-cart-name .icon {
  flex: none;
  color: var(--unread-count);
}

/* prices by item (3.8): the list and an item's chart */
.prices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.prices-bar {
  display: flex;
  gap: 8px;
}

.prices-search {
  flex: 1;
  min-width: 0;
}

.prices-bar select {
  flex: none;
}

.pick-list li > button.price-row {
  gap: 10px;
}

.price-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.price-name {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.price-spark {
  flex: none;
  color: var(--primary);
}

.price-side {
  flex: none;
  min-width: 72px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.price-last {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.price-change {
  font-size: 0.8rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.price-change.up {
  color: var(--danger);
}

.price-change.down {
  color: var(--ok);
}

.price-change.same {
  color: var(--muted);
}

.prices-note,
.price-summary {
  margin: 0;
}

/* SVG: geometry in attributes, colors here (the CSP takes no inline style) */
.price-chart {
  display: block;
  width: 100%;
  height: auto;
  color: var(--primary);
}

.price-chart .price-grid {
  color: var(--line);
}

.price-chart .price-axis {
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}

.price-chart .price-dot {
  fill: var(--primary);
  stroke: var(--panel);
}

.price-chart .price-point {
  cursor: pointer;
}

.price-chart .price-point:hover .price-dot,
.price-chart .price-point.picked .price-dot {
  fill: var(--text);
}

.price-chart .price-tip rect {
  fill: var(--panel);
  stroke: var(--line);
}

.price-chart .price-tip text {
  fill: var(--text);
  font-variant-numeric: tabular-nums;
}

.price-points .price-point-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 8px 6px;
}

.price-point-main {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-weight: 600;
}

.price-point-price {
  font-variant-numeric: tabular-nums;
}

.price-point-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0 12px;
}

.price-point-actions button.link {
  padding: 6px 0;
}

/* the family's location screen (4.6) */
.location-screen .map-tab {
  flex: none;
}

.loc-section h3 {
  margin: 0 0 4px;
  font-size: 1.05rem;
}

.loc-section > button.secondary,
.loc-section > button.link {
  align-self: flex-start;
}

.loc-section > button.primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.loc-rooms {
  overflow-wrap: anywhere;
}

.my-shares {
  list-style: none;
  margin: 0;
  padding: 0;
}

.my-share {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
}

.my-share + .my-share {
  border-top: 1px solid var(--line);
}

.my-share-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.my-share-room {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.my-share button.secondary {
  flex: none;
  min-height: 40px;
}

.loc-place-names {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
}

.loc-trails {
  align-self: flex-start;
}

.loc-section p {
  margin: 0;
}
